import React, { useState, useEffect, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import '../components/modules/dubz-assistant.css';
import DubzGalaxyAnimation from '../components/modules/DubzGalaxyAnimation';

const DubzAssistant = () => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isActive, setIsActive] = useState(false);

  const audioRef = useRef(null);
  const recognitionRef = useRef(null);

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      console.log('Browser does not support speech recognition');
      return;
    }

    // Check for microphone permission
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        setHasPermission(true);
      })
      .catch((err) => {
        console.log('Microphone permission denied:', err);
        setHasPermission(false);
      });

    // بارگذاری صداهای موجود
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        const voices = window.speechSynthesis.getVoices();
        console.log('تعداد صداهای موجود:', voices.length);
        console.log(
          'صداهای فارسی:',
          voices.filter((v) => v.lang.includes('fa')),
        );
        console.log(
          'همه صداها:',
          voices.map((v) => `${v.name} (${v.lang})`),
        );
      };
    }
  }, [browserSupportsSpeechRecognition]);

  // تابع محاسبه فاصله Levenshtein
  function getLevenshteinDistance(a, b) {
    const an = a ? a.length : 0;
    const bn = b ? b.length : 0;
    if (an === 0) return bn;
    if (bn === 0) return an;
    const matrix = [];
    for (let i = 0; i <= bn; ++i) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= an; ++j) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= bn; ++i) {
      for (let j = 1; j <= an; ++j) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1, // deletion
          );
        }
      }
    }
    return matrix[bn][an];
  }

  // تابع محاسبه درصد شباهت
  function getSimilarity(a, b) {
    const distance = getLevenshteinDistance(a, b);
    const maxLen = Math.max(a.length, b.length);
    if (maxLen === 0) return 1;
    return (maxLen - distance) / maxLen;
  }

  // لیست فرمان‌ها و پاسخ‌ها
  const voiceCommands = [
    {
      keywords: [
        'دابزی خودت رو معرفی کن',
        'خودت رو معرفی کن',
        'تو کی هستی',
        'دابزی کی هستی',
        'خودت رو بگو',
        'دابزی خودت رو بگو',
        'تو چی هستی',
        'دابزی چی هستی',
        'خودت رو توضیح بده',
        'دابزی خودت رو توضیح بده',
        'تو چیکار می‌کنی',
        'دابزی چیکار می‌کنی',
        'خودت رو معرفی کن دابزی',
        'دابزی خودت رو بگو',
        'تو کی هستی دابزی',
        'دابزی تو کی هستی',
        'خودت رو معرفی کن بگو',
        'دابزی خودت رو معرفی کن بگو',
        'تو چی هستی دابزی',
        'دابزی تو چی هستی',
        'خودت رو بگو دابزی',
        'دابزی خودت رو بگو',
        'تو چیکار می‌کنی دابزی',
        'دابزی تو چیکار می‌کنی',
        'خودت رو توضیح بده دابزی',
        'دابزی خودت رو توضیح بده',
        'تو کی هستی بگو',
        'دابزی تو کی هستی بگو',
        'خودت رو معرفی کن بگو دابزی',
        'دابزی خودت رو معرفی کن بگو',
        'تو چی هستی بگو',
        'دابزی تو چی هستی بگو',
        'خودت رو بگو بگو',
        'دابزی خودت رو بگو بگو',
        'تو چیکار می‌کنی بگو',
        'دابزی تو چیکار می‌کنی بگو',
        'خودت رو توضیح بده بگو',
        'دابزی خودت رو توضیح بده بگو',
        'تو کی هستی بگو دابزی',
        'دابزی تو کی هستی بگو',
        'خودت رو معرفی کن بگو بگو',
        'دابزی خودت رو معرفی کن بگو بگو',
        'تو چی هستی بگو دابزی',
        'دابزی تو چی هستی بگو',
        'خودت رو بگو بگو دابزی',
        'دابزی خودت رو بگو بگو',
        'تو چیکار می‌کنی بگو دابزی',
        'دابزی تو چیکار می‌کنی بگو',
        'خودت رو توضیح بده بگو دابزی',
        'دابزی خودت رو توضیح بده بگو',
        'تو کی هستی بگو بگو',
        'دابزی تو کی هستی بگو بگو',
        'خودت رو معرفی کن بگو بگو دابزی',
        'دابزی خودت رو معرفی کن بگو بگو',
        'تو چی هستی بگو بگو',
        'دابزی تو چی هستی بگو بگو',
        'خودت رو بگو بگو',
        'دابزی خودت رو بگو بگو',
        'تو چیکار می‌کنی بگو بگو',
        'دابزی تو چیکار می‌کنی بگو بگو',
        'خودت رو توضیح بده بگو بگو',
        'دابزی خودت رو توضیح بده بگو بگو',
      ],
      answer:
        'من یه دستیار صوتی کاملا هوشمند هستم، می‌تونی دابزی صدام کنی و هرکاری بخوای برات انجام می‌دم، کافیه صدام کنی',
    },
    {
      keywords: [
        'تعداد آگهی',
        'چند تا آگهی',
        'آگهی های من چقدره',
        'آگهی من چقدره',
        'آگهی هام چندتاست',
        'چند آگهی دارم',
        'تعداد آگهی هام',
        'آگهی هام چند تا',
        'چند تا آگهی دارم',
        'آگهی های من چند تا',
      ],
      answer: '۲۸ تا',
    },
    {
      keywords: [
        'آگهی هام چقدر دیده شدن',
        'آگهی هام چقدر بازدید',
        'آگهی هام بازدید روزانه',
        'آگهی هام چند بازدید',
        'آگهی های من چقدر بازدید',
        'بازدید آگهی هام',
        'آگهی هام چقدر دیده شده',
        'چقدر بازدید دارم',
        'بازدید روزانه آگهی هام',
        'آگهی هام چند نفر دیده',
      ],
      answer: 'میانگین بین ۵ بازدید تا ۷ بازدید روزانه',
    },
    {
      keywords: [
        'قیمت دلار',
        'دلار چنده',
        'دلار چند',
        'دلار امروز',
        'قیمت دلار چنده',
        'دلار چند تومانه',
        'دلار امروز چنده',
        'قیمت دلار امروز',
        'دلار چقدره',
        'دلار چند تومان',
      ],
      answer: '۸۸ هزار و ۴۰۰ تومان',
    },
  ];

  useEffect(() => {
    if (transcript && isListening) {
      const userMessage = transcript.trim();
      console.log('تشخیص داده شده:', userMessage); // برای debug

      // فرمان‌های خاص با تشخیص هوشمند
      let foundCommand = null;
      let bestSimilarity = 0;

      voiceCommands.forEach((cmd) => {
        cmd.keywords.forEach((keyword) => {
          const similarity = getSimilarity(userMessage, keyword);
          console.log(`شباهت "${userMessage}" با "${keyword}":`, similarity);

          if (similarity > bestSimilarity && similarity >= 0.6) {
            bestSimilarity = similarity;
            foundCommand = cmd;
          }
        });
      });

      if (foundCommand) {
        console.log('فرمان پیدا شد:', foundCommand.answer, 'با شباهت:', bestSimilarity);
        speakText(foundCommand.answer);
        setConversation((prev) => [
          ...prev,
          { type: 'user', text: transcript },
          { type: 'assistant', text: foundCommand.answer },
        ]);
        resetTranscript();
        return;
      }

      // روش‌های مختلف تشخیص سلام دابزی
      const methods = [
        // روش 1: بررسی دقیق سلام دابزی
        () => {
          const cleanMessage = userMessage.replace(/\s/g, '').toLowerCase();
          return (
            cleanMessage.includes('سلامدابزی') || cleanMessage.includes('سلامدابز') || cleanMessage.includes('سلامدوزی')
          );
        },
        // روش 2: بررسی با فاصله
        () => {
          const words = userMessage.toLowerCase().split(/\s+/);
          return (
            words.includes('سلام') && (words.includes('دابزی') || words.includes('دابز') || words.includes('دوزی'))
          );
        },
        // روش 3: بررسی شباهت
        () => {
          if (userMessage.startsWith('سلام')) {
            const afterSalam = userMessage.replace(/^سلام\s*/, '').replace(/\s/g, '');
            if (afterSalam.length > 0) {
              const similarity = getSimilarity(afterSalam, 'دابزی');
              console.log('شباهت با دابزی:', similarity); // برای debug
              return similarity >= 0.6; // کاهش آستانه
            }
          }
          return false;
        },
      ];

      // اگر هر کدام از روش‌ها موفق بود
      const shouldRespond = methods.some((method) => method());
      console.log('باید پاسخ دهد:', shouldRespond); // برای debug

      if (shouldRespond) {
        handleDubzGreeting();
        resetTranscript();
      }
    }
  }, [transcript, isListening]);

  const handleDubzGreeting = () => {
    const response = 'سلام محمد، امروز چکار میتونم برات انجام بدم';
    speakText(response);

    setConversation((prev) => [...prev, { type: 'user', text: transcript }, { type: 'assistant', text: response }]);
  };

  const speakText = (text) => {
    // روش 1: Web Speech API (رایگان و قابل دسترس)
    if ('speechSynthesis' in window) {
      setIsSpeaking(true);
      window.speechSynthesis.cancel();

      const voices = window.speechSynthesis.getVoices();
      const maleVoice = voices.find(
        (voice) => voice.lang === 'fa-IR' && voice.name === 'Microsoft Farid Online (Natural) - Persian (Iran)',
      );

      let utterance;
      if (maleVoice) {
        utterance = new SpeechSynthesisUtterance(text);
        utterance.voice = maleVoice;
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 1;
      } else {
        utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.pitch = 0.9;
        utterance.volume = 1;
      }
      utterance.lang = 'fa-IR';

      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(utterance);
    } else {
      // روش 2: استفاده از API های رایگان جایگزین
      console.log('Web Speech API در دسترس نیست، از API جایگزین استفاده می‌شود');
      setIsSpeaking(false);
    }
  };

  // API های رایگان جایگزین
  const useAlternativeTTS = async (text) => {
    try {
      // استفاده از API رایگان TTS
      const response = await fetch('https://api.voicerss.org/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          key: 'd9d5b82d1d924f6b9157c69e2e5af877', // نیاز به ثبت‌نام رایگان
          hl: 'fa-ir',
          src: text,
          c: 'MP3',
          f: '44khz_16bit_stereo',
        }),
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.play();
        setIsSpeaking(false);
      }
    } catch (error) {
      console.log('خطا در استفاده از API جایگزین:', error);
      setIsSpeaking(false);
    }
  };

  const startListening = () => {
    if (!hasPermission) {
      alert('لطفاً دسترسی میکروفون را فعال کنید');
      return;
    }

    setIsListening(true);
    setIsActive(true);
    SpeechRecognition.startListening({
      continuous: true,
      language: 'fa-IR',
      interimResults: true,
    });
  };

  const stopListening = () => {
    setIsListening(false);
    setIsActive(false);
    SpeechRecognition.stopListening();
    resetTranscript();
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const requestMicrophonePermission = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        setHasPermission(true);
        alert('دسترسی میکروفون فعال شد!');
      })
      .catch((err) => {
        console.log('Microphone permission denied:', err);
        alert('دسترسی میکروفون رد شد. لطفاً دوباره تلاش کنید.');
      });
  };

  if (!browserSupportsSpeechRecognition) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">مرورگر شما پشتیبانی نمی‌کند</h2>
            <p className="text-gray-600 mb-6">
              دستیار صوتی دابزی نیاز به مرورگر مدرن دارد که از Web Speech API پشتیبانی کند.
            </p>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">پیشنهاد می‌کنیم از Chrome، Firefox یا Safari استفاده کنید.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {/* {isSpeaking && <DubzGalaxyAnimation />} */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">دستیار صوتی دابزی</h1>
          <p className="text-gray-600">برای شروع صحبت، روی دکمه کلیک کنید</p>
        </div>

        {/* Permission Check */}
        {!hasPermission && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <span className="text-yellow-800 font-medium">دسترسی میکروفون نیاز است</span>
            </div>
            <button
              onClick={requestMicrophonePermission}
              className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              فعال‌سازی دسترسی
            </button>
          </div>
        )}

        {/* Main Assistant Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={toggleListening}
            disabled={!hasPermission || isSpeaking}
            className={`
              relative w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300 dubz-main-button
              ${
                isListening
                  ? 'listening-active shadow-lg shadow-red-200'
                  : isSpeaking
                    ? 'speaking-active shadow-lg shadow-green-200'
                    : 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-200 hover:shadow-xl dubz-glow'
              }
              ${!hasPermission || isSpeaking ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {/* Multiple Ripple Effects */}
            {isListening && (
              <>
                <div className="absolute inset-0 rounded-full bg-red-400 dubz-pulse-ring"></div>
                <div className="absolute inset-0 rounded-full bg-red-300 dubz-wave"></div>
              </>
            )}

            {/* Voice Visualizer when listening */}
            {isListening && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="voice-visualizer">
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                  <div className="voice-bar"></div>
                </div>
              </div>
            )}

            {/* Icon */}
            <div className="relative z-10">
              {isListening ? (
                <svg className="w-12 h-12 text-white dubz-float" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : isSpeaking ? (
                <svg className="w-12 h-12 text-white dubz-float" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
              ) : (
                <svg className="w-12 h-12 text-white dubz-float" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              )}
            </div>
          </button>
        </div>

        {/* Status Indicators */}
        <div className="flex justify-center space-x-4 mb-6">
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${isListening ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`}
            ></div>
            <span className="text-sm text-gray-600">در حال گوش دادن</span>
          </div>
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}
            ></div>
            <span className="text-sm text-gray-600">در حال صحبت</span>
          </div>
        </div>

        {/* Transcript Display */}
        {transcript && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">متن تشخیص داده شده:</h3>
            <p className="text-gray-800 text-right leading-relaxed">{transcript}</p>
          </div>
        )}

        {/* Conversation History */}
        {conversation.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">تاریخچه گفتگو:</h3>
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {conversation.map((msg, index) => (
                <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                      msg.type === 'user' ? 'bg-blue-500 text-white' : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">نحوه استفاده:</h3>
          <ul className="text-sm text-blue-700 space-y-1 text-right">
            <li>• روی دکمه میکروفون کلیک کنید</li>
            <li>• بگویید: "سلام دابزی"</li>
            <li>• منتظر پاسخ دستیار باشید</li>
          </ul>

          {/* دکمه تست */}
          <div className="mt-4 pt-4 border-t border-blue-200">
            <button
              onClick={() => {
                console.log('تست دستی');
                handleDubzGreeting();
              }}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              تست دستی پاسخ
            </button>

            {/* دکمه‌های تست فرمان‌ها */}
            <div className="mt-3 space-y-2">
              <button
                onClick={() => {
                  const cmd = voiceCommands[0];
                  speakText(cmd.answer);
                  setConversation((prev) => [
                    ...prev,
                    { type: 'user', text: 'دابزی خودت رو معرفی کن' },
                    { type: 'assistant', text: cmd.answer },
                  ]);
                }}
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
              >
                تست: دابزی معرفی
              </button>

              <button
                onClick={() => {
                  const cmd = voiceCommands[1];
                  speakText(cmd.answer);
                  setConversation((prev) => [
                    ...prev,
                    { type: 'user', text: 'تعداد آگهی‌های من چقدره؟' },
                    { type: 'assistant', text: cmd.answer },
                  ]);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
              >
                تست: تعداد آگهی
              </button>

              <button
                onClick={() => {
                  const cmd = voiceCommands[2];
                  speakText(cmd.answer);
                  setConversation((prev) => [
                    ...prev,
                    { type: 'user', text: 'آگهی‌هام چقدر دیده شدن؟' },
                    { type: 'assistant', text: cmd.answer },
                  ]);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
              >
                تست: بازدید آگهی
              </button>

              <button
                onClick={() => {
                  const cmd = voiceCommands[3];
                  speakText(cmd.answer);
                  setConversation((prev) => [
                    ...prev,
                    { type: 'user', text: 'قیمت دلار چقدره؟' },
                    { type: 'assistant', text: cmd.answer },
                  ]);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
              >
                تست: قیمت دلار
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DubzAssistant;
